/**
 * Shared drag-resize for dock panels (width or height).
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

export type DockResizeAxis = "x" | "y";

/**
 * `start` = left/top edge (drag toward start grows the panel).
 * `end` = right/bottom edge (drag toward end grows the panel).
 */
export type DockResizeEdge = "start" | "end";

export function useDockResize(options: {
  storedSize: number;
  clamp: (raw: unknown) => number;
  onCommit: (size: number) => void;
  axis: DockResizeAxis;
  edge: DockResizeEdge;
}) {
  const { storedSize, clamp, onCommit, axis, edge } = options;
  const [size, setSize] = useState(() => clamp(storedSize));
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ startPointer: number; startSize: number } | null>(null);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const clampRef = useRef(clamp);
  clampRef.current = clamp;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    if (!dragging) {
      setSize(clamp(storedSize));
    }
  }, [storedSize, dragging, clamp]);

  const commit = useCallback((next: number) => {
    const clamped = clampRef.current(next);
    setSize(clamped);
    onCommitRef.current(clamped);
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startPointer: axis === "x" ? event.clientX : event.clientY,
      startSize: sizeRef.current,
    };
    setDragging(true);
  }, [axis]);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const pointer = axis === "x" ? event.clientX : event.clientY;
    const delta = pointer - drag.startPointer;
    // start edge: moving pointer toward lower coords grows (invert delta)
    const signed = edge === "start" ? -delta : delta;
    setSize(clampRef.current(drag.startSize + signed));
  }, [axis, edge]);

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!dragRef.current) {
      return;
    }
    dragRef.current = null;
    setDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Already released.
    }
    commit(sizeRef.current);
  }, [commit]);

  const nudge = useCallback((delta: number) => {
    commit(sizeRef.current + delta);
  }, [commit]);

  return {
    size,
    dragging,
    sizeRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    nudge,
  };
}
